import type { BlockBundle, Severity } from "../types"
import { CANONICAL_EXPR_PREFIXES } from "../vocab/expression-namespaces"
import { resolveRuntimeAction } from "../vocab/runtime-action-resolution"

export type StateScope =
  | "appData"
  | "local"
  | "computed"
  | "props"
  | "event"
  | "item"
  | "_results"
  | "env"
  | "route"
  | "_system"
  | "unknown"

export interface StateRef {
  path: string
  scope: StateScope
  declared: boolean
  readonly: boolean
  type?: string
  ownerBlock?: string
}

export type StateMutationOperation =
  | "set"
  | "append"
  | "upsert"
  | "removeWhere"
  | "updateWhere"
  | "compute"

export interface StateWrite extends StateRef {
  operation: StateMutationOperation
  valueExpression?: string
}

export interface StateTransition {
  id: string
  blockId: string
  triggerEvent?: string
  flow: string
  stepPath: string
  action: string
  guard?: string
  reads: StateRef[]
  writes: StateWrite[]
}

export interface StateTransitionIssue {
  rule: string
  level: Severity
  message: string
  flow?: string
  stepPath?: string
  path?: string
}

export interface StateTransitionReport {
  blockId: string
  transitions: StateTransition[]
  issues: StateTransitionIssue[]
  uiReads: StateRef[]
  declarations: StateRef[]
}

export interface StateTransitionAnalyzerOptions {
  appConfig?: Record<string, unknown>
  siblingBlocks?: Record<string, BlockBundle>
}

interface Declaration {
  path: string
  scope: StateScope
  readonly: boolean
  type?: string
  ownerBlock?: string
}

interface AnalysisContext {
  blockId: string
  declarations: Map<string, Declaration>
  appDataDeclarations: Map<string, Declaration>
  inputPaths: Set<string>
  outputPaths: Set<string>
  uiReadPaths: Set<string>
  uiReads: StateRef[]
}

const EXPR_RX = /#\{\s*([^}]+?)\s*\}/g
const CONTROL_FLOW_TYPES = new Set(["conditional", "loop", "switch", "parallel", "tryCatch", "stage", "saga"])
// Exported for the ontology drift-binding test (state-transition-ontology-parity.test.ts).
// The TTL twin lives in kg-browser fixtures/actionflow-state-transitions-v1.ttl
// (castx:op-* cogo:requiresTargetType / castx:scope-* cogo:readonlyScope).
export const ARRAY_OPERATIONS = new Set<StateMutationOperation>(["append", "upsert", "removeWhere", "updateWhere"])
export const READONLY_SCOPES = new Set<StateScope>(["computed", "props", "event", "item", "env", "route", "_system"])
// Scopes whose writes additionally require a declared contract path (rules
// state-machine-appdata-declared + state-machine-write-permission). TTL twin:
// castx:scope-* cogo:requiresWritePermission true.
export const WRITE_PERMISSION_SCOPES = new Set<StateScope>(["appData"])
// Every scope carrying a StateAccessPolicy in the TTL = the full StateScope
// enum minus 'unknown' (absence of policy = forbidden). Drives prefix
// recognition in normalizeStatePath.
//
// Copy #6 of the expression-prefix vocabulary (found by the derivation gate,
// not by the census): this used to hand-list the canonical 9 a second time.
// It is now derived, so it cannot drift from the ontology. The one member it
// adds beyond the canonical enum is named and reasoned:
//   `_system` — the runtime's internal bookkeeping scope. It carries a
//   StateAccessPolicy (read-only) in actionflow-state-transitions-v1.ttl but is
//   deliberately not an authoring prefix, so it is absent from the canonical
//   enum in expression-prefix-v1.ttl.
const POLICY_SCOPE_EXTRAS: readonly StateScope[] = ["_system"]
export const POLICY_SCOPES = new Set<StateScope>([
  ...([...CANONICAL_EXPR_PREFIXES] as StateScope[]),
  ...POLICY_SCOPE_EXTRAS,
])
// Full closed operation union. TTL twin: castx:op-* cogo:name.
export const STATE_MUTATION_OPERATIONS = new Set<StateMutationOperation>([
  "set", "append", "upsert", "removeWhere", "updateWhere", "compute",
])
// Mirrors actionSetVariable's SCOPE_NS routing in
// cogo-runtime-v2/src/action-flow-engine.ts — scope decides WHERE the
// variable is written, so the static write must land in the same scope.
const SET_VARIABLE_SCOPE_NS: Record<string, string> = {
  app: "appData",
  appData: "appData",
  global: "appData",
  local: "local",
  computed: "computed",
}

// Mirrors the engine's standalone array-mutation aliases ("Aliases" block in
// action-flow-engine.ts): flow.json may use these as action names instead of
// setState with an operation parameter.
const SET_STATE_ALIAS_OPERATIONS: Record<string, StateMutationOperation> = {
  upsertWhere: "upsert",
  removeWhere: "removeWhere",
  updateWhere: "updateWhere",
}

// Actions whose executor writes params.resultPath (default shown) — verified
// against the engine's platform-delegated action implementations. restApi is
// special-cased in collectStepWrites (resultTarget ?? target, no default).
const RESULT_PATH_ACTION_DEFAULTS: Record<string, string | undefined> = {
  storage: undefined,
  clipboardRead: "_results.clipboard",
  geolocation: "_results.geo",
  fileUpload: "_results.file",
  pickFiles: "_results.pickFiles",
  cryptoAction: "_results.crypto",
}

// Mirrors symbol-store LEGACY_READ_ALIAS (runtime-v2): legacy heads the
// runtime resolves to local on read; the analyzer must classify them the
// same way or projection matching diverges from runtime behavior.
const LEGACY_HEAD_ALIAS: Record<string, string> = {
  state: "local",
  localState: "local",
}

// Identifier heads that are never state references inside #{...} expressions.
const EXPR_SKIP_IDENTIFIERS = new Set(["Math", "JSON", "true", "false", "null", "undefined", "typeof", "new", "in", "of"])

// User-observable host effects with no symbol-store write — an event-bound
// flow containing one of these is not a no-effect flow. Every member must
// exist in the engine executor dispatch table; the drift lock is
// state-transition-host-effect-parity.test.ts against actionflow-executors-v1.ttl
// (generated from action-flow-engine.ts builtinActions). callFlow is NOT a
// member: it is resolved transitively in flowGuaranteesEffect.
export const HOST_EFFECT_ACTIONS = new Set([
  "navigate",
  "goLink",
  "showPopup",
  "openModal",
  "showToast",
  "showLoading",
  "emitEvent",
  "invokeCallback",
  "mediaControl",
  "copyToClipboard",
  "scrollToElement",
  "scrollToPosition",
  "setTheme",
  "notification",
  "shareAction",
  "downloadAction",
  "printAction",
  "openAuthSession",
  "payment",
  "confirmAction",
  "csvExport",
  "jsonExport",
])

export function analyzeStateTransitions(
  bundle: BlockBundle,
  options: StateTransitionAnalyzerOptions = {},
): StateTransitionReport {
  const blockId = readBlockId(bundle)
  const ctx = buildContext(bundle, blockId, options.appConfig)
  const transitions: StateTransition[] = []
  const issues: StateTransitionIssue[] = []
  const eventByFlow = collectEventBindings(bundle.events)
  const flows = readFlows(bundle.flow)

  for (const [flowName, flowDef] of Object.entries(flows)) {
    const steps = Array.isArray((flowDef as Record<string, unknown>)?.steps)
      ? ((flowDef as Record<string, unknown>).steps as unknown[])
      : []
    walkSteps({
      ctx,
      flowName,
      steps,
      eventLabel: eventByFlow.get(flowName),
      guard: undefined,
      pathPrefix: "",
      itemVars: [],
      transitions,
      issues,
    })
  }

  for (const transition of transitions) {
    if (transition.guard && transition.writes.length > 0) {
      issues.push({
        rule: "state-machine-guarded-transition",
        level: "info",
        message: `Transition writes ${transition.writes.map((write) => `'${write.path}'`).join(", ")} only when guard '${transition.guard}' holds - statically reachable, runtime-unproven`,
        flow: transition.flow,
        stepPath: transition.stepPath,
      })
    }
    for (const write of transition.writes) {
      collectWriteIssues(ctx, transition, write, issues)
    }
  }

  collectEventEffectIssues(ctx, flows, eventByFlow, transitions, issues)

  return {
    blockId,
    transitions,
    issues,
    uiReads: ctx.uiReads,
    declarations: [...ctx.declarations.values()].map((decl) => ({
      path: decl.path,
      scope: decl.scope,
      declared: true,
      readonly: decl.readonly,
      type: decl.type,
      ownerBlock: decl.ownerBlock,
    })),
  }
}

function readBlockId(bundle: BlockBundle): string {
  const metadata = bundle.block.metadata as Record<string, unknown> | undefined
  return String(metadata?.id ?? bundle.block.name ?? "unknown-block")
}

function buildContext(
  bundle: BlockBundle,
  blockId: string,
  appConfig?: Record<string, unknown>,
): AnalysisContext {
  const declarations = new Map<string, Declaration>()
  const appDataDeclarations = new Map<string, Declaration>()
  const inputPaths = new Set<string>()
  const outputPaths = new Set<string>()

  const addDecl = (input: string, type?: string, ownerBlock = blockId): void => {
    const ref = normalizeStatePath(input)
    const decl: Declaration = {
      path: ref.path,
      scope: ref.scope,
      readonly: READONLY_SCOPES.has(ref.scope),
      type,
      ownerBlock,
    }
    declarations.set(ref.path, decl)
    if (ref.scope === "appData") appDataDeclarations.set(ref.path, decl)
  }

  collectLocalDeclarations(bundle.variables, addDecl)
  collectStateContract(bundle.block, addDecl, inputPaths, outputPaths)
  collectAppDeclarations(appConfig, (path, type) => {
    const ref = normalizeStatePath(path.startsWith("appData.") ? path : `appData.${path}`)
    const decl: Declaration = {
      path: ref.path,
      scope: "appData",
      readonly: false,
      type,
      ownerBlock: "app",
    }
    declarations.set(ref.path, decl)
    appDataDeclarations.set(ref.path, decl)
  })

  const uiReads = collectUiReads(bundle.ui, declarations, blockId)
  const uiReadPaths = new Set(uiReads.map((read) => read.path))

  return {
    blockId,
    declarations,
    appDataDeclarations,
    inputPaths,
    outputPaths,
    uiReadPaths,
    uiReads,
  }
}

function collectLocalDeclarations(
  variables: Record<string, unknown>,
  addDecl: (path: string, type?: string) => void,
): void {
  const buckets = [
    variables.state,
    variables.localState,
    variables.computed,
  ] as Array<unknown>

  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== "object") continue
    for (const [key, value] of Object.entries(bucket as Record<string, unknown>)) {
      addDecl(key.startsWith("computed.") ? key : `local.${key}`, readDeclaredType(value))
    }
  }
}

function collectStateContract(
  block: Record<string, unknown>,
  addDecl: (path: string, type?: string) => void,
  inputPaths: Set<string>,
  outputPaths: Set<string>,
): void {
  const contract = block.stateContract as Record<string, unknown> | undefined
  if (!contract || typeof contract !== "object") return

  const localState = contract.localState as Record<string, unknown> | undefined
  if (localState && typeof localState === "object") {
    for (const [key, value] of Object.entries(localState)) {
      addDecl(`local.${key}`, readDeclaredType(value))
    }
  }

  collectSymbolPaths(contract.inputSymbols, inputPaths)
  collectSymbolPaths(contract.outputSymbols, outputPaths)
  for (const path of inputPaths) addDecl(path)
  for (const path of outputPaths) addDecl(path)
}

function collectSymbolPaths(value: unknown, paths: Set<string>): void {
  if (!Array.isArray(value)) return
  for (const item of value) {
    if (!item || typeof item !== "object") continue
    const obj = item as Record<string, unknown>
    const raw = typeof obj.path === "string" ? obj.path : typeof obj.name === "string" ? obj.name : undefined
    if (!raw) continue
    paths.add(normalizeStatePath(raw).path)
  }
}

function collectAppDeclarations(
  appConfig: Record<string, unknown> | undefined,
  addDecl: (path: string, type?: string) => void,
): void {
  if (!appConfig) return
  collectAppDeclarationBucket(appConfig.appData, addDecl)
  collectAppDeclarationBucket(appConfig.globalState, addDecl)
  const meta = appConfig.meta as Record<string, unknown> | undefined
  const contract = meta?.stateContract as Record<string, unknown> | undefined
  if (contract) {
    collectAppDeclarationBucket(contract.inputSymbols, addDecl)
    collectAppDeclarationBucket(contract.outputSymbols, addDecl)
  }
}

function collectAppDeclarationBucket(
  bucket: unknown,
  addDecl: (path: string, type?: string) => void,
): void {
  if (Array.isArray(bucket)) {
    for (const item of bucket) {
      if (!item || typeof item !== "object") continue
      const obj = item as Record<string, unknown>
      const path = typeof obj.path === "string" ? obj.path : typeof obj.name === "string" ? obj.name : undefined
      if (path) addDecl(path, readDeclaredType(obj))
    }
    return
  }
  if (!bucket || typeof bucket !== "object") return
  for (const [key, value] of Object.entries(bucket as Record<string, unknown>)) {
    addDecl(key, readDeclaredType(value))
  }
}

function collectUiReads(
  ui: Record<string, unknown>,
  declarations: Map<string, Declaration>,
  blockId: string,
): StateRef[] {
  const paths = new Set<string>()
  walkUnknown(ui, (value) => {
    if (typeof value !== "string") return
    for (const expr of extractExpressionBodies(value)) {
      for (const ref of extractStateRefs(expr)) paths.add(ref.path)
    }
  })
  return [...paths].map((path) => stateRefFromPath(path, declarations, blockId))
}

function collectEventBindings(events: Record<string, unknown>): Map<string, string> {
  const eventByFlow = new Map<string, string>()
  const remember = (eventLabel: string, binding: unknown): void => {
    if (typeof binding === "string") {
      eventByFlow.set(binding, eventLabel)
      return
    }
    if (!binding || typeof binding !== "object") return
    const flow = (binding as Record<string, unknown>).flow
    if (typeof flow === "string") eventByFlow.set(flow, eventLabel)
  }

  const bindings = events.bindings as Record<string, unknown> | undefined
  if (bindings && typeof bindings === "object") {
    for (const [key, binding] of Object.entries(bindings)) remember(key, binding)
  }

  const elements = events.elements as Record<string, unknown> | undefined
  if (elements && typeof elements === "object") {
    for (const [elementId, eventMap] of Object.entries(elements)) {
      if (!eventMap || typeof eventMap !== "object") continue
      for (const [eventType, binding] of Object.entries(eventMap as Record<string, unknown>)) {
        remember(`${elementId}:${eventType}`, binding)
      }
    }
  }

  for (const [elementId, eventMap] of Object.entries(events)) {
    if (elementId === "bindings" || elementId === "elements" || elementId === "lifecycle") continue
    if (!eventMap || typeof eventMap !== "object") continue
    for (const [eventType, binding] of Object.entries(eventMap as Record<string, unknown>)) {
      remember(`${elementId}:${eventType}`, binding)
    }
  }

  return eventByFlow
}

function readFlows(flow: Record<string, unknown>): Record<string, unknown> {
  const nested = flow.flows
  if (nested && typeof nested === "object") return nested as Record<string, unknown>
  return flow
}

function walkSteps(args: {
  ctx: AnalysisContext
  flowName: string
  steps: unknown[]
  eventLabel?: string
  guard?: string
  pathPrefix: string
  itemVars: readonly string[]
  transitions: StateTransition[]
  issues: StateTransitionIssue[]
}): void {
  const { ctx, flowName, steps, eventLabel, guard, pathPrefix, itemVars, transitions, issues } = args
  for (let index = 0; index < steps.length; index++) {
    const step = steps[index]
    if (!step || typeof step !== "object") continue
    const obj = step as Record<string, unknown>
    const stepPath = pathPrefix ? `${pathPrefix}.${index}` : String(index)
    const type = typeof obj.type === "string" ? obj.type : "basic"

    if (type === "conditional") {
      const condition = typeof obj.condition === "string" ? obj.condition : undefined
      const thenSteps = readStepArray(obj.then)
      const elseSteps = readStepArray(obj.else)
      const thenWrites = countStateWrites(thenSteps)
      const elseWrites = countStateWrites(elseSteps)
      if (condition && thenWrites !== elseWrites && (thenWrites === 0 || elseWrites === 0)) {
        issues.push({
          rule: "state-machine-guarded-transition-incomplete",
          level: "warning",
          message: `Conditional '${condition}' has a possible branch without a state transition`,
          flow: flowName,
          stepPath,
        })
      }
      walkSteps({ ...args, steps: thenSteps, guard: condition, pathPrefix: `${stepPath}.then` })
      walkSteps({ ...args, steps: elseSteps, guard: condition ? `!(${condition})` : undefined, pathPrefix: `${stepPath}.else` })
      continue
    }

    if (type === "switch") {
      const cases = obj.cases
      if (cases && typeof cases === "object") {
        for (const [caseName, caseSteps] of Object.entries(cases as Record<string, unknown>)) {
          walkSteps({ ...args, steps: readStepArray(caseSteps), guard: `case:${caseName}`, pathPrefix: `${stepPath}.cases.${caseName}` })
        }
      }
      walkSteps({ ...args, steps: readStepArray(obj.default), guard: "default", pathPrefix: `${stepPath}.default` })
      continue
    }

    if (type === "loop") {
      const itemVar = typeof obj.itemVar === "string" && obj.itemVar.length > 0 ? obj.itemVar : undefined
      walkSteps({
        ...args,
        steps: readStepArray(obj.steps ?? obj.body),
        guard,
        pathPrefix: `${stepPath}.body`,
        itemVars: itemVar ? [...itemVars, itemVar] : itemVars,
      })
      continue
    }

    if (type === "tryCatch") {
      walkSteps({ ...args, steps: readStepArray(obj.try), guard, pathPrefix: `${stepPath}.try` })
      walkSteps({ ...args, steps: readStepArray(obj.catch), guard: "catch", pathPrefix: `${stepPath}.catch` })
      walkSteps({ ...args, steps: readStepArray(obj.finally), guard, pathPrefix: `${stepPath}.finally` })
      continue
    }

    if (type === "parallel") {
      walkSteps({ ...args, steps: readStepArray(obj.steps), guard, pathPrefix: `${stepPath}.steps` })
      continue
    }

    if (type === "stage") {
      for (const [field, branchGuard] of [
        ["onEnter", guard],
        ["steps", guard],
        ["onSuccess", "stage:success"],
        ["onError", "stage:error"],
        ["onFinally", guard],
        ["compensate", "saga:compensate"],
      ] as const) {
        walkSteps({ ...args, steps: readStepArray(obj[field]), guard: branchGuard, pathPrefix: `${stepPath}.${field}` })
      }
      continue
    }

    if (type === "saga") {
      walkSteps({ ...args, steps: readStepArray(obj.stages), guard, pathPrefix: `${stepPath}.stages` })
      continue
    }

    if (CONTROL_FLOW_TYPES.has(type)) continue

    const action = typeof obj.action === "string" ? obj.action : ""
    if (!action) continue
    const canonicalAction = resolveRuntimeAction(action)?.canonicalAction ?? action
    const params = obj.params && typeof obj.params === "object" ? obj.params as Record<string, unknown> : {}
    const reads = collectStepReads(params, ctx, itemVars)
    const writes = collectStepWrites(canonicalAction, params, ctx, itemVars)
    if (reads.length === 0 && writes.length === 0) continue
    transitions.push({
      id: `${ctx.blockId}:${flowName}:${stepPath}`,
      blockId: ctx.blockId,
      triggerEvent: eventLabel,
      flow: flowName,
      stepPath,
      action,
      guard,
      reads,
      writes,
    })
  }
}

function collectEventEffectIssues(
  ctx: AnalysisContext,
  flows: Record<string, unknown>,
  eventByFlow: Map<string, string>,
  transitions: StateTransition[],
  issues: StateTransitionIssue[],
): void {
  for (const [flowName, eventLabel] of eventByFlow.entries()) {
    const flowDef = flows[flowName]
    if (!flowDef || typeof flowDef !== "object") continue
    if (flowGuaranteesEffect(flowName, flows, transitions, new Set())) continue

    issues.push({
      rule: "state-machine-event-has-effect",
      level: "warning",
      message: `Event '${eventLabel}' triggers flow '${flowName}' but no state transition or host effect is guaranteed`,
      flow: flowName,
    })
  }
}

// A flow guarantees an effect when it (or any flow it reaches via callFlow,
// cycle-guarded) writes state or performs a host-visible action.
function flowGuaranteesEffect(
  flowName: string,
  flows: Record<string, unknown>,
  transitions: StateTransition[],
  visited: Set<string>,
): boolean {
  if (visited.has(flowName)) return false
  visited.add(flowName)
  if (transitions.some((transition) => transition.flow === flowName && transition.writes.length > 0)) return true
  const flowDef = flows[flowName]
  if (!flowDef || typeof flowDef !== "object") return false
  const steps = readStepArray((flowDef as Record<string, unknown>).steps)
  return stepsHaveHostEffect(steps, (target) => flowGuaranteesEffect(target, flows, transitions, visited))
}

function stepsHaveHostEffect(steps: unknown[], resolveCallFlow?: (flowName: string) => boolean): boolean {
  for (const step of steps) {
    if (!step || typeof step !== "object") continue
    const obj = step as Record<string, unknown>
    const action = typeof obj.action === "string" ? obj.action : ""
    const canonicalAction = resolveRuntimeAction(action)?.canonicalAction ?? action
    if (HOST_EFFECT_ACTIONS.has(canonicalAction)) return true
    if (canonicalAction === "callFlow" && resolveCallFlow) {
      const params = obj.params && typeof obj.params === "object" ? obj.params as Record<string, unknown> : {}
      // Mirrors actionCallFlow: params.flow ?? params.flowName.
      const target = readStringParam(params.flow) ?? readStringParam(params.flowName)
      if (target && resolveCallFlow(target)) return true
    }
    const type = typeof obj.type === "string" ? obj.type : "basic"
    if (type === "conditional" && (
      stepsHaveHostEffect(readStepArray(obj.then), resolveCallFlow) ||
      stepsHaveHostEffect(readStepArray(obj.else), resolveCallFlow)
    )) return true
    if (type === "switch") {
      const cases = obj.cases
      if (cases && typeof cases === "object") {
        for (const caseSteps of Object.values(cases as Record<string, unknown>)) {
          if (stepsHaveHostEffect(readStepArray(caseSteps), resolveCallFlow)) return true
        }
      }
      if (stepsHaveHostEffect(readStepArray(obj.default), resolveCallFlow)) return true
    }
    if (type === "loop" && stepsHaveHostEffect(readStepArray(obj.steps ?? obj.body), resolveCallFlow)) return true
    if (type === "parallel" && stepsHaveHostEffect(readStepArray(obj.steps), resolveCallFlow)) return true
    if (type === "tryCatch" && (
      stepsHaveHostEffect(readStepArray(obj.try), resolveCallFlow) ||
      stepsHaveHostEffect(readStepArray(obj.catch), resolveCallFlow) ||
      stepsHaveHostEffect(readStepArray(obj.finally), resolveCallFlow)
    )) return true
    if (type === "stage" && ["onEnter", "steps", "onSuccess", "onError", "onFinally", "compensate"].some(
      (field) => stepsHaveHostEffect(readStepArray(obj[field]), resolveCallFlow),
    )) return true
    if (type === "saga" && stepsHaveHostEffect(readStepArray(obj.stages), resolveCallFlow)) return true
  }
  return false
}

function countStateWrites(steps: unknown[]): number {
  let count = 0
  for (const step of steps) {
    if (!step || typeof step !== "object") continue
    const obj = step as Record<string, unknown>
    const action = typeof obj.action === "string" ? obj.action : ""
    const canonicalAction = resolveRuntimeAction(action)?.canonicalAction ?? action
    if (canonicalAction === "setState" || canonicalAction === "compute" || canonicalAction === "setVariable") count++
    const type = typeof obj.type === "string" ? obj.type : "basic"
    if (type === "conditional") {
      count += countStateWrites(readStepArray(obj.then))
      count += countStateWrites(readStepArray(obj.else))
    }
    if (type === "switch") {
      const cases = obj.cases
      if (cases && typeof cases === "object") {
        for (const caseSteps of Object.values(cases as Record<string, unknown>)) {
          count += countStateWrites(readStepArray(caseSteps))
        }
      }
      count += countStateWrites(readStepArray(obj.default))
    }
    if (type === "loop") count += countStateWrites(readStepArray(obj.steps ?? obj.body))
    if (type === "parallel") count += countStateWrites(readStepArray(obj.steps))
    if (type === "tryCatch") {
      count += countStateWrites(readStepArray(obj.try))
      count += countStateWrites(readStepArray(obj.catch))
      count += countStateWrites(readStepArray(obj.finally))
    }
    if (type === "stage") {
      for (const field of ["onEnter", "steps", "onSuccess", "onError", "onFinally", "compensate"]) {
        count += countStateWrites(readStepArray(obj[field]))
      }
    }
    if (type === "saga") count += countStateWrites(readStepArray(obj.stages))
  }
  return count
}

function readStepArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function collectStepReads(
  params: Record<string, unknown>,
  ctx: AnalysisContext,
  itemVars: readonly string[] = [],
): StateRef[] {
  const paths = new Set<string>()
  walkUnknown(params, (value) => {
    if (typeof value !== "string") return
    for (const expr of extractExpressionBodies(value)) {
      for (const ref of extractStateRefs(expr, itemVars)) paths.add(ref.path)
    }
  })
  return [...paths].map((path) => stateRefFromPath(path, ctx.declarations, ctx.blockId))
}

function collectStepWrites(
  action: string,
  params: Record<string, unknown>,
  ctx: AnalysisContext,
  itemVars: readonly string[] = [],
): StateWrite[] {
  const valueExpression = typeof params.value === "string"
    ? params.value
    : typeof params.expression === "string"
      ? params.expression
      : undefined

  const aliasOperation = SET_STATE_ALIAS_OPERATIONS[action]
  if (aliasOperation) {
    const target = typeof params.target === "string" ? params.target : undefined
    if (!target) return []
    const ref = stateRefFromPath(target, ctx.declarations, ctx.blockId, itemVars)
    return [{ ...ref, operation: aliasOperation, valueExpression }]
  }

  if (action === "restApi" || action in RESULT_PATH_ACTION_DEFAULTS) {
    const raw = action === "restApi"
      ? readStringParam(params.resultTarget) ?? readStringParam(params.target)
      : readStringParam(params.resultPath) ??
        (action === "pickFiles" ? readStringParam(params.resultTarget) : undefined) ??
        RESULT_PATH_ACTION_DEFAULTS[action]
    if (!raw) return []
    const ref = stateRefFromPath(raw, ctx.declarations, ctx.blockId, itemVars)
    return [{ ...ref, operation: "set", valueExpression: undefined }]
  }

  if (action !== "setState" && action !== "compute" && action !== "setVariable") return []
  let rawTarget: string | undefined
  if (action === "setVariable") {
    // Mirror actionSetVariable: name ?? target, and scope routes the write
    // into a namespace prefix (app/appData/global → appData, computed, local).
    rawTarget = readStringParam(params.name) ?? readStringParam(params.target)
    if (!rawTarget) return []
    const scope = params.scope != null ? String(params.scope) : ""
    if (scope) rawTarget = `${SET_VARIABLE_SCOPE_NS[scope] ?? scope}.${rawTarget}`
  } else {
    rawTarget = readStringParam(params.target) ?? readStringParam(params.name)
    if (!rawTarget) return []
  }
  const ref = stateRefFromPath(rawTarget, ctx.declarations, ctx.blockId, itemVars)
  const operation = normalizeOperation(action, params.operation)
  return [{ ...ref, operation, valueExpression }]
}

function readStringParam(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined
}

function collectWriteIssues(
  ctx: AnalysisContext,
  transition: StateTransition,
  write: StateWrite,
  issues: StateTransitionIssue[],
): void {
  const common = {
    flow: transition.flow,
    stepPath: transition.stepPath,
    path: write.path,
  }

  if (write.readonly) {
    issues.push({
      ...common,
      rule: "state-machine-readonly-write",
      level: "error",
      message: `ActionFlow writes readonly state '${write.path}'`,
    })
  }

  if (WRITE_PERMISSION_SCOPES.has(write.scope) && !isPathCovered(write.path, ctx.appDataDeclarations)) {
    issues.push({
      ...common,
      rule: "state-machine-appdata-declared",
      level: "warning",
      message: `ActionFlow writes '${write.path}' but no application global declaration covers it`,
    })
  }

  if (WRITE_PERMISSION_SCOPES.has(write.scope) && !isPathAllowed(write.path, ctx.outputPaths)) {
    issues.push({
      ...common,
      rule: "state-machine-write-permission",
      level: "warning",
      message: `Block '${ctx.blockId}' writes '${write.path}' without a matching stateContract.outputSymbols path`,
    })
  }

  if (write.scope === "local" && ctx.outputPaths.has(write.path)) {
    issues.push({
      ...common,
      rule: "state-machine-local-leak",
      level: "error",
      message: `Block-local state '${write.path}' is exposed as an output symbol`,
    })
  }

  if (ARRAY_OPERATIONS.has(write.operation) && write.type && write.type !== "array") {
    issues.push({
      ...common,
      rule: "state-machine-array-operation-target",
      level: "warning",
      message: `setState operation '${write.operation}' expects array target but '${write.path}' is declared as '${write.type}'`,
    })
  }

  if (write.scope !== "_results" && write.path !== "appData.lastAction" && !isPathAllowed(write.path, ctx.uiReadPaths)) {
    issues.push({
      ...common,
      rule: "state-machine-ui-projection-missing",
      level: "warning",
      message: `State write '${write.path}' has no UI projection in this block`,
    })
  }
}

function normalizeOperation(action: string, operation: unknown): StateMutationOperation {
  if (action === "compute") return "compute"
  const op = typeof operation === "string" ? operation : "set"
  if (ARRAY_OPERATIONS.has(op as StateMutationOperation)) return op as StateMutationOperation
  return "set"
}

function extractExpressionBodies(input: string): string[] {
  const bodies: string[] = []
  EXPR_RX.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = EXPR_RX.exec(input)) !== null) bodies.push(match[1].trim())
  return bodies
}

function extractStateRefs(expr: string, itemVars: readonly string[] = []): StateRef[] {
  const refs: StateRef[] = []
  const seen = new Set<string>()
  // Quoted text is not a state path (e.g. replace('a.b', 'c')).
  const stripped = expr.replace(/'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g, "''")
  const rx = /[a-zA-Z_$][a-zA-Z0-9_$]*(?:\.[a-zA-Z0-9_$-]+)*/g
  let match: RegExpExecArray | null
  while ((match = rx.exec(stripped)) !== null) {
    let token = match[0]
    if (stripped[match.index + token.length] === "(") {
      // A call: keep the receiver of a method call (local.items.map → local.items);
      // a bare function head (formatCurrency, $sum, cuid) is not a state ref.
      const lastDot = token.lastIndexOf(".")
      if (lastDot < 0) continue
      token = token.slice(0, lastDot)
    }
    const head = token.split(".")[0]
    if (EXPR_SKIP_IDENTIFIERS.has(head) || head.startsWith("$")) continue
    const ref = normalizeStatePath(token, itemVars)
    if (ref.scope === "unknown" || seen.has(ref.path)) continue
    seen.add(ref.path)
    refs.push({ ...ref, declared: false, readonly: READONLY_SCOPES.has(ref.scope) })
  }
  return refs
}

function stateRefFromPath(
  input: string,
  declarations: Map<string, Declaration>,
  blockId: string,
  itemVars: readonly string[] = [],
): StateRef {
  const normalized = normalizeStatePath(input, itemVars)
  const decl = findCoveringDeclaration(normalized.path, declarations)
  return {
    path: normalized.path,
    scope: normalized.scope,
    declared: Boolean(decl),
    readonly: READONLY_SCOPES.has(normalized.scope) || decl?.readonly === true,
    type: decl?.type,
    ownerBlock: decl?.ownerBlock ?? (normalized.scope === "local" ? blockId : undefined),
  }
}

function normalizeStatePath(input: string, itemVars: readonly string[] = []): { path: string; scope: StateScope } {
  let path = input.trim()
  if (path.startsWith("#{") && path.endsWith("}")) path = path.slice(2, -1).trim()
  if (path.startsWith("#")) path = path.slice(1).trim()
  if (path.startsWith("@")) path = `local.${path.slice(1)}`
  const segments = path.split(".")
  const head = segments[0]
  // An itemVar declared by an enclosing loop renames the readonly item. scope.
  if (itemVars.includes(head)) {
    const rest = segments.slice(1).join(".")
    return { path: rest ? `item.${rest}` : "item", scope: "item" }
  }
  if (LEGACY_HEAD_ALIAS[head]) {
    path = [LEGACY_HEAD_ALIAS[head], ...segments.slice(1)].join(".")
  }
  const first = path.split(".")[0] as StateScope
  if (POLICY_SCOPES.has(first)) {
    return { path, scope: first }
  }
  if (/^[a-zA-Z_][a-zA-Z0-9_$-]*(?:\.[a-zA-Z0-9_$-]+)*$/.test(path)) {
    return { path: `local.${path}`, scope: "local" }
  }
  return { path, scope: "unknown" }
}

function findCoveringDeclaration(
  path: string,
  declarations: Map<string, Declaration>,
): Declaration | undefined {
  if (declarations.has(path)) return declarations.get(path)
  for (const [declPath, decl] of declarations) {
    if (path.startsWith(`${declPath}.`) || declPath.startsWith(`${path}.`)) return decl
  }
  return undefined
}

function isPathCovered(path: string, declarations: Map<string, Declaration>): boolean {
  return Boolean(findCoveringDeclaration(path, declarations))
}

function isPathAllowed(path: string, allowedPaths: Set<string>): boolean {
  if (allowedPaths.has(path)) return true
  for (const allowed of allowedPaths) {
    if (path.startsWith(`${allowed}.`) || allowed.startsWith(`${path}.`)) return true
  }
  return false
}

function readDeclaredType(value: unknown): string | undefined {
  if (value && typeof value === "object" && "type" in value) {
    const type = (value as Record<string, unknown>).type
    return typeof type === "string" ? type : undefined
  }
  if (Array.isArray(value)) return "array"
  if (value === null || value === undefined) return undefined
  return typeof value
}

function walkUnknown(value: unknown, visit: (value: unknown) => void): void {
  visit(value)
  if (!value || typeof value !== "object") return
  if (Array.isArray(value)) {
    for (const item of value) walkUnknown(item, visit)
    return
  }
  for (const child of Object.values(value as Record<string, unknown>)) {
    walkUnknown(child, visit)
  }
}
